import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetPlanById, useGetTaskList, useUpdatedPlanStatusMutation } from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { setPlanData } from "@/redux/slices";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components";
import { planStatus } from "@/lib/utils";

// export const data = [
//   {
//     name: "1. Pre Construction",
//     type: "category",
//     children: [
//       {
//         name: "Subcategory 1",
//         type: "subcategory",
//         children: [
//           {
//             name: "Task 1",
//             duration: "04 Days",
//             start: "18-02-2026",
//             end: "22-02-2026",
//           },
//           {
//             name: "Task 2",
//             duration: "04 Days",
//             start: "18-02-2026",
//             end: "22-02-2026",
//           },
//         ],
//       },
//     ],
//   },
//   {
//     name: "2. Pre Construction",
//     children: [
//       {
//         name: "Subcategory 2",
//         children: [
//           {
//             name: "Task 3",
//             duration: "04 Days",
//             start: "18-02-2026",
//             end: "22-02-2026",
//           },
//           {
//             name: "Task 4",
//             duration: "04 Days",
//             start: "18-02-2026",
//             end: "22-02-2026",
//           },
//         ],
//       },
//     ],
//   },
// ];

const useEditPlan = () => {
  const {id, action, planId, planAction} = useParams();
  const isView = action == routes.view || planAction == routes.view;
  const isEdit = action == routes.edit || planAction == routes.edit;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [previewPlan, setPreviewPlan] = useState<boolean>(false);
  const [reason, setReason] = useState<string>("");
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const { projectData, planData: planDetails } = useSelector(
    (state: RootState) => state.currentData
  );
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {data: planData} = useGetPlanById({
    planId: planId as string,
    projectId: projectData?.projectId,
    queryParams: {
      enabled: !!(planId && projectData?.projectId)
    }
  })

  const isPlanDraft = useMemo(() => planData?.data?.status == planStatus.draft.value, [planData]);

  useEffect(() => {
    if (planData && JSON.stringify(planData?.data) != JSON.stringify(planDetails)){
      dispatch(setPlanData(planData?.data))
    }
  }, [planData]);

  const {data: taskList} = useGetTaskList({
    planId: planId as string,
    projectId: projectData?.projectId,
    queryParams: {
      enabled: !!(planId && projectData?.projectId && isPlanDraft)
    }
  });

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.pm}/${routes.projectOverview}`,
        name: `Project Overview`,
      },
      {
        link: `/${routes.pm}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}`,
        name:
          isEdit || isView
            ? `${projectData?.name} Project Details`
            : "Add Project",
      },
      {
        link: "",
        name: `${planData?.data?.name} Details`
      },
    ],
    [projectData, planData]
  );
  
  const isTaskExist = useMemo(() => {
    return taskList?.data?.length;
  }, [taskList]);

  const onPreviewPlan = () => {
    setPreviewPlan((prev: boolean) => !prev);
  }

  const { mutate: updateStatus, isPending: isUpdatePending } = useUpdatedPlanStatusMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["plan-list", id],
      });
      setShowConfirmation(false);
      setReason("");
      toast({ message: "Plan submitted for approval." });
    },
  });

  const onSubmitApproval = () => {
    setShowConfirmation(true);
  }

  const onChangeReason = (e: any) => {
    setReason(e.target.value)
  }

  const onConfirmSubmit = () => {
    if (reason?.length < 21) {
      toast({type: 'error', message: 'Minimum 20 character required for reason'});
      return;
    }

    updateStatus({
      planId: planId as string,
      projectId: id as string,
      data: {
        action: planStatus.approval_pending.value,
        comment: reason
      }
    })
  }

  return {
    breadcrumbData,
    onPreviewPlan,
    previewPlan,
    showConfirmation,
    setShowConfirmation,
    onConfirmSubmit,
    onSubmitApproval,
    reason,
    onChangeReason,
    isUpdatePending,
    isPlanDraft,
    isTaskExist,
  }
}

export default useEditPlan;